/**
 * Cache utility for storing and retrieving data from localStorage
 * Can be disabled via REACT_APP_DISABLE_CACHE environment variable
 */

const CACHE_PREFIX = 'game_bet_app_';
const GAMES_CACHE_KEY = `${CACHE_PREFIX}games`;
const CACHE_TIMESTAMP_KEY = `${CACHE_PREFIX}games_timestamp`;

// Default cache duration: 5 minutes (in milliseconds)
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000;

// Check if caching is disabled via environment variable
const isCacheDisabled = () => {
  return process.env.REACT_APP_DISABLE_CACHE === 'true' || 
         process.env.REACT_APP_DISABLE_CACHE === '1';
};

/**
 * Get cached games data if available and not expired
 * @param {number} maxAge - Maximum age of cache in milliseconds (default: 5 minutes)
 * @returns {Array|null} - Cached games array or null if not available/expired
 */
export const getCachedGames = (maxAge = DEFAULT_CACHE_DURATION) => {
  if (isCacheDisabled()) {
    console.log('🚫 Cache is disabled via REACT_APP_DISABLE_CACHE');
    return null;
  }

  try {
    const cachedData = localStorage.getItem(GAMES_CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (!cachedData || !timestamp) {
      return null;
    }

    const now = Date.now();
    const cacheAge = now - parseInt(timestamp, 10);

    if (cacheAge > maxAge) {
      console.log('⏰ Cache expired, removing old data');
      clearGamesCache();
      return null;
    }

    const games = JSON.parse(cachedData);
    console.log(`✅ Using cached games data (age: ${Math.round(cacheAge / 1000)}s)`);
    return games;
  } catch (error) {
    console.error('Error reading from cache:', error);
    clearGamesCache();
    return null;
  }
};

/**
 * Save games data to cache
 * @param {Array} games - Games array to cache
 */
export const setCachedGames = (games) => {
  if (isCacheDisabled()) {
    console.log('🚫 Cache is disabled, not saving to cache');
    return;
  }

  try {
    localStorage.setItem(GAMES_CACHE_KEY, JSON.stringify(games));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    console.log('💾 Games data cached successfully');
  } catch (error) {
    console.error('Error saving to cache:', error);
    // If quota exceeded, clear old cache and try again
    if (error.name === 'QuotaExceededError') {
      console.warn('Storage quota exceeded, clearing cache');
      clearGamesCache();
    }
  }
};

/**
 * Clear games cache
 */
export const clearGamesCache = () => {
  try {
    localStorage.removeItem(GAMES_CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    console.log('🗑️ Games cache cleared');
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Get cache info (for debugging)
 * @returns {Object|null} - Cache info or null if no cache
 */
export const getCacheInfo = () => {
  try {
    const cachedData = localStorage.getItem(GAMES_CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (!cachedData || !timestamp) {
      return null;
    }

    const now = Date.now();
    const cacheAge = now - parseInt(timestamp, 10);
    const games = JSON.parse(cachedData);

    return {
      gamesCount: games.length,
      age: cacheAge,
      ageFormatted: `${Math.round(cacheAge / 1000)}s`,
      timestamp: new Date(parseInt(timestamp, 10)).toISOString(),
      isExpired: cacheAge > DEFAULT_CACHE_DURATION,
      isDisabled: isCacheDisabled(),
    };
  } catch (error) {
    console.error('Error getting cache info:', error);
    return null;
  }
};
